import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import UnitPlayer from './UnitPlayer.jsx';
import NativeUnitPlayer from './NativeUnitPlayer.jsx';
import DnaLesson from './DnaLesson.jsx';
import FractionFoundry from './FractionFoundry.jsx';
import SilentFrame from './SilentFrame.jsx';

const GITHUB_BASE = '/curriculum';
const CACHE_KEY  = 'lumio_cv11_';  // bump this string to invalidate all cached lesson JSON
const CACHE_TTL  = 60 * 60 * 1000;

// Learn starts at Year 6 (owner 2026-11-03: Years 3–5 removed from Learn completely; exams keep their own year lists).
const YEARS = [
  { id: 'year6',  label: 'Year 6',  number: '6'  },
  { id: 'year7',  label: 'Year 7',  number: '7'  },
  { id: 'year8',  label: 'Year 8',  number: '8'  },
  { id: 'year9',  label: 'Year 9',  number: '9'  },
  { id: 'year10', label: 'Year 10', number: '10' },
  { id: 'year11', label: 'Year 11', number: '11' },
  { id: 'year12', label: 'Year 12', number: '12' },
];

// A year counts as available once it has real coverage across more than one
// subject: a single isolated subject reads as pilot content, not a launched
// year, so it stays gated until more subjects land. Computed from the same
// /curriculum catalog LearnSection already fetches, never hardcoded.
const MIN_SUBJECTS_FOR_YEAR = 2;

// Icon/colour fallback map (year index doesn't include these)
const SUBJECT_META = {
  mathematics:      { icon: '📐', color: '#3B82F6' },
  english:          { icon: '📖', color: '#8B5CF6' },
  science:          { icon: '🔬', color: '#10B981' },
  biology:          { icon: '🧬', color: '#34D399' },
  chemistry:        { icon: '⚗️', color: '#F59E0B' },
  physics:          { icon: '⚡', color: '#60A5FA' },
  history:          { icon: '🏛️', color: '#EF4444' },
  geography:        { icon: '🌏', color: '#10B981' },
  drama:            { icon: '🎭', color: '#A855F7' },
  music:            { icon: '🎵', color: '#14B8A6' },
  visual_arts:      { icon: '🎨', color: '#EC4899' },
  pdhpe:            { icon: '🏃', color: '#F97316' },
  psychology:       { icon: '🧠', color: '#6366F1' },
  business_studies: { icon: '💼', color: '#D97706' },
  economics:        { icon: '📊', color: '#06B6D4' },
  legal_studies:    { icon: '⚖️', color: '#EF4444' },
  technologies:     { icon: '💻', color: '#0EA5E9' },
  legal:            { icon: '⚖️', color: '#EF4444' },
  business:         { icon: '💼', color: '#D97706' },
  religion:         { icon: '📜', color: '#8B5CF6' },
  society:          { icon: '👥', color: '#0EA5E9' },
  vce:              { icon: '🎓', color: '#4F46E5' },
  hass:             { icon: '🌏', color: '#F59E0B' },
  civics:           { icon: '🗳️', color: '#2563EB' },
  digitaltech:      { icon: '💻', color: '#0EA5E9' },
  hpe:              { icon: '🏃', color: '#F97316' },
  earthspace:       { icon: '🪐', color: '#6366F1' },
  earthenv:         { icon: '🌋', color: '#B45309' },
  investigating:    { icon: '🧪', color: '#10B981' },
  cafs:             { icon: '👪', color: '#F97316' },
  hms:              { icon: '💪', color: '#F97316' },
  pe:               { icon: '⚽', color: '#F97316' },
  hhd:              { icon: '🍎', color: '#EF4444' },
  ancient:          { icon: '🏺', color: '#B45309' },
  'ancient-history': { icon: '🏺', color: '#B45309' },
  modern:           { icon: '🗞️', color: '#EF4444' },
  'modern-history': { icon: '🗞️', color: '#EF4444' },
  'australian-history': { icon: '🦘', color: '#EF4444' },
  revolutions:      { icon: '🚩', color: '#EF4444' },
  'business-management': { icon: '💼', color: '#D97706' },
  enterprise:       { icon: '💻', color: '#0EA5E9' },
  software:         { icon: '⌨️', color: '#0EA5E9' },
};

// Light "Flight Deck" theme — matches the /app Home dashboard system
// (design-refs/Lumio Flight Deck standalone.html). `serif` is kept as a
// token name to minimise the diff below, but now points at the Space
// Grotesk heading font rather than an actual serif.
const L = {
  bg:      '#EEEDF3',
  surface: '#FFFFFF',
  card:    '#FFFFFF',
  border:  '#ECEAF3',
  text:    '#16162A',
  textMid: '#6B6A82',
  textDim: '#8B87A8',
  accent:  '#6C5CE7',
  radius:  14,
  shadow:  '0 1px 2px rgba(30,22,70,.04), 0 12px 30px rgba(40,30,90,.06)',
  font:    "'Hanken Grotesk', sans-serif",
  serif:   "'Space Grotesk', sans-serif",
  mono:    "'Space Mono', monospace",
};

// ── GitHub fetch with sessionStorage cache ────────────────────────

async function fetchCurriculum(path) {
  const key = `${CACHE_KEY}${path}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts < CACHE_TTL) return data;
    }
  } catch {}
  const res = await fetch(`${GITHUB_BASE}/${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  try { sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
  return data;
}

// ── Resume-on-refresh breadcrumb ────────────────────────────────────
// Remembers year/subject/unit so a page refresh (or reopening the app a
// while later, e.g. closing the laptop overnight) drops the student back
// where they were instead of the year picker. Same localStorage + staleness
// pattern as lumio_exam_session in lumio.jsx. Only consulted once per real
// page load (guarded by a module-level flag, not React state, so it
// naturally resets on refresh but does NOT re-fire just from clicking into
// Learn again later in the same session).
const NAV_KEY = 'lumio_learn_nav';
const NAV_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours
let hasRestoredLearnNav = false;

function saveNav(nav) {
  try {
    if (nav) localStorage.setItem(NAV_KEY, JSON.stringify({ ...nav, savedAt: Date.now() }));
    else localStorage.removeItem(NAV_KEY);
  } catch {}
}

// The last topic this child opened, kept (unlike the 24-hour nav above) so Today can offer "Carry on".
// Per child, the same way topic pages key their progress (the first child keeps the bare key).
const activeChild = () => { try { return localStorage.getItem('lumio_active_profile') || ''; } catch { return ''; } };
export const lastUnitKey = (profileId = activeChild()) => (profileId ? `lumio_last_unit_${profileId}` : 'lumio_last_unit');
function rememberUnit(unit, nav) {
  try { localStorage.setItem(lastUnitKey(), JSON.stringify({ ...nav, unitId: unit.unit_id, title: unit.title, lessons: unit.lesson_count || 0, savedAt: Date.now() })); } catch {}
}
// Open Learn straight at a saved position (Today's "Carry on"): the next LearnSection mount drills down to it.
export function resumeLearnAt(nav) {
  saveNav({ year: nav.year, subject: nav.subject, subGroup: nav.subGroup || null, unitFile: nav.unitFile });
  hasRestoredLearnNav = false;
}

function loadNavOnce() {
  if (hasRestoredLearnNav) return null;
  hasRestoredLearnNav = true;
  try {
    const raw = localStorage.getItem(NAV_KEY);
    if (!raw) return null;
    const nav = JSON.parse(raw);
    if (!nav.savedAt || Date.now() - nav.savedAt > NAV_MAX_AGE) return null;
    return nav;
  } catch { return null; }
}

// ── HTML Unit Embed ───────────────────────────────────────────────
// Loads the self-contained HTML via a bundled JS module (dynamic import)
// so it never depends on server routing — on Railway the /units/* route
// was falling through to the SPA catch-all, serving index.html instead
// of the actual cold-war content, causing recursive SPA nesting.
// Portal into document.body bypasses all ancestor stacking contexts.
function HtmlUnitEmbed({ embedPath, title, onClose, onNavigateProgress }) {
  const [srcdoc, setSrcdoc] = useState(null);
  const [fetchErr, setFetchErr] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Cold War is bundled inline — all other lessons are served at their URL
    // so we use iframe src (not srcDoc) to keep relative asset paths intact.
    if (embedPath === 'units/cold-war-unit.html') {
      import('../assets/generated/coldWarUnit')
        .then(m => { if (!cancelled) setSrcdoc(m.default); })
        .catch(err => { if (!cancelled) setFetchErr(err.message); });
    }
    return () => { cancelled = true; };
  }, [embedPath]);

  // Lesson URL for iframe src — relative asset paths resolve correctly from /lessons/
  const lessonUrl = embedPath !== 'units/cold-war-unit.html' ? `/${embedPath}` : null;

  // Some units (e.g. the English/Technologies "menu" units) are just an
  // index page of plain <a href> links to separate per-lesson files —
  // clicking one navigates the iframe itself via real browser navigation,
  // invisible to React. A page refresh reloads the iframe back to
  // `lessonUrl` (the menu), losing which sub-lesson was open. Track the
  // iframe's own location on every same-origin load and resume from there
  // next time, same 24h staleness window used elsewhere.
  const iframeNavKey = `lumio_iframe_nav:${embedPath}`;
  const [iframeSrc] = useState(() => {
    if (!lessonUrl) return null;
    try {
      const raw = localStorage.getItem(iframeNavKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.path && Date.now() - (saved.savedAt || 0) < 24 * 60 * 60 * 1000) {
          return saved.path;
        }
      }
    } catch {}
    return lessonUrl;
  });

  function handleIframeLoad() {
    if (!lessonUrl) return; // cold-war srcDoc path — nothing to track
    try {
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (!win) return;
      const path = win.location.pathname + win.location.search;
      localStorage.setItem(iframeNavKey, JSON.stringify({ path, savedAt: Date.now() }));
    } catch {
      // cross-origin or otherwise inaccessible — nothing we can do, skip silently
    }
  }

  return createPortal(
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, left: 0,
        zIndex: 2147483647,
        display: 'flex', flexDirection: 'column',
        background: L.bg,
        pointerEvents: 'auto',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        background: L.surface,
        borderBottom: `1px solid ${L.border}`,
        flexShrink: 0,
      }}>
        <button onClick={onClose} style={{
          background: '#F5F4FA', border: `1px solid ${L.border}`,
          borderRadius: 8, width: 36, height: 36,
          color: L.textMid, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontFamily: L.font, flexShrink: 0,
        }}>←</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: L.text, fontFamily: L.font, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        {onNavigateProgress && (
          <button onClick={onNavigateProgress} style={{
            background: `${L.accent}12`, border: `1px solid ${L.accent}44`,
            borderRadius: 8, padding: '0 12px', height: 36,
            color: L.accent, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 12, fontWeight: 700, fontFamily: L.font, flexShrink: 0,
          }}>📊 My Progress</button>
        )}
      </div>
      {fetchErr ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444', fontFamily: L.font, fontSize: 14, padding: 24 }}>
          Failed to load unit: {fetchErr}
        </div>
      ) : lessonUrl ? (
        <SilentFrame
          ref={iframeRef}
          src={iframeSrc}
          onLoad={handleIframeLoad}
          title={title}
          style={{ flex: 1, border: 'none', width: '100%', display: 'block' }}
          allow="fullscreen"
        />
      ) : srcdoc ? (
        <SilentFrame
          srcDoc={srcdoc}
          title={title}
          style={{ flex: 1, border: 'none', width: '100%', display: 'block' }}
          allow="fullscreen"
        />
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: L.bg }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${L.border}`, borderTopColor: L.accent, animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}
    </div>,
    document.body
  );
}

// ── Sub-components ────────────────────────────────────────────────

function BackButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: L.card, border: `1px solid ${L.border}`, borderRadius: 8,
      width: 32, height: 32, color: L.textMid, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, flexShrink: 0, fontFamily: L.font,
    }}>←</button>
  );
}

function YearPills({ selected, onSelect, availability }) {
  return (
    <div style={{
      display: 'flex', gap: 8, overflowX: 'auto', padding: '10px 16px',
      background: L.surface, borderBottom: `1px solid ${L.border}`,
      flexShrink: 0, msOverflowStyle: 'none', scrollbarWidth: 'none',
    }}>
      {YEARS.map(y => {
        // Until availability has loaded, don't gate anything (avoids a flash of
        // disabled pills). Once loaded, a year with no real content is inert -
        // same rule the year-picker grid already applies via YearCard.
        const available = availability === null || availability === undefined || !!availability[y.id];
        return (
          <button key={y.id} onClick={() => available && onSelect(y.id)} disabled={!available} style={{
            padding: '5px 14px', borderRadius: 99,
            border: `1.5px solid ${selected === y.id ? L.accent : L.border}`,
            background: selected === y.id ? L.accent : 'transparent',
            color: selected === y.id ? '#FFFFFF' : available ? L.textMid : L.textDim,
            fontSize: 11, fontWeight: 700, cursor: available ? 'pointer' : 'default',
            opacity: available ? 1 : 0.45,
            fontFamily: L.font, flexShrink: 0, transition: 'all 0.15s',
          }}>{y.label}</button>
        );
      })}
    </div>
  );
}

const CARD_SHADOW = '0 1px 2px rgba(30,22,70,.04), 0 12px 30px rgba(40,30,90,.06)';
const CARD_SHADOW_HOVER = '0 2px 4px rgba(30,22,70,.05), 0 18px 40px rgba(40,30,90,.11)';

function YearCard({ year, active, onSelect }) {
  const tile = (
    <div style={{ width: 42, height: 42, borderRadius: 13, background: '#EDE8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
      <span style={{ fontFamily: L.serif, fontWeight: 700, fontSize: 15, color: '#6C5CE7' }}>{year.number}</span>
    </div>
  );
  const heading = (
    <div style={{ fontFamily: L.serif, fontWeight: 700, fontSize: 17, color: '#16162A', marginBottom: 4 }}>{year.label}</div>
  );

  if (!active) {
    return (
      <div style={{
        background: '#FFFFFF', border: '1px solid #ECEAF3', borderRadius: 22,
        boxShadow: CARD_SHADOW, padding: '18px 17px', opacity: 0.55, fontFamily: L.font,
      }}>
        {tile}
        {heading}
        <div style={{ fontFamily: L.mono, fontWeight: 700, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8B87A8' }}>Coming soon</div>
      </div>
    );
  }

  return (
    <button onClick={onSelect}
      style={{
        background: '#FFFFFF', border: '1px solid #ECEAF3', borderRadius: 22,
        boxShadow: CARD_SHADOW, padding: '18px 17px', cursor: 'pointer', textAlign: 'left',
        fontFamily: L.font, transition: 'transform .18s cubic-bezier(.2,.75,.2,1), box-shadow .18s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = CARD_SHADOW_HOVER; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = CARD_SHADOW; }}
    >
      {tile}
      {heading}
      <div style={{ fontFamily: L.mono, fontWeight: 700, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: '#6C5CE7' }}>Tap to explore</div>
    </button>
  );
}

function Spinner({ label }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: L.bg }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${L.border}`, borderTopColor: L.accent, animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <span style={{ fontSize: 13, color: L.textMid, fontFamily: L.font }}>{label || 'Loading…'}</span>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{ margin: 16, background: '#FEF2F2', border: `2px solid #EF4444`, borderRadius: L.radius, padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 14, color: '#991B1B', fontFamily: L.font, fontWeight: 700 }}>Error loading unit</span>
      <span style={{ fontSize: 12, color: '#B91C1C', fontFamily: L.mono, wordBreak: 'break-all' }}>{message}</span>
      {onRetry && <button onClick={onRetry} style={{ background: '#EF4444', border: 'none', borderRadius: 8, color: 'white', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: L.font, alignSelf: 'flex-start' }}>Retry</button>}
    </div>
  );
}

// Grid of subject tiles — used both for the top-level Year subject picker
// and for a subject that itself groups further subjects (Science's
// Biology/Chemistry/Physics/Earth & Space) one level deeper.
function SubjectTiles({ subjects, onSelect, emptyTitle, emptyBody, noun = 'unit', parentIcon = '📚' }) {
  if (subjects.length === 0) {
    return (
      <div style={{ background: L.card, border: `1.5px dashed ${L.border}`, borderRadius: L.radius, padding: '36px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>🚧</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: L.text, marginBottom: 8, fontFamily: L.serif }}>{emptyTitle}</div>
        <div style={{ fontSize: 13, color: L.textMid, lineHeight: 1.65 }}>{emptyBody}</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {subjects.map(sub => {
        const meta = SUBJECT_META[sub.subject_id] || { icon: parentIcon, color: L.accent };
        const hasLessons = (sub.unit_count || 0) > 0;
        if (!hasLessons) {
          return (
            <div key={sub.subject_id}
              style={{ background: L.card, border: `1.5px solid ${L.border}`, borderRadius: L.radius, padding: '16px 14px', fontFamily: L.font, boxShadow: L.shadow, opacity: 0.55 }}
            >
              <div style={{ fontSize: 24, marginBottom: 8 }}>{meta.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: L.text, marginBottom: 3, lineHeight: 1.3 }}>{sub.title}</div>
              <div style={{ fontSize: 10, color: L.textDim, fontFamily: L.mono, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Coming soon</div>
            </div>
          );
        }
        return (
          <button key={sub.subject_id} onClick={() => onSelect(sub.subject_id)}
            style={{ background: L.card, border: `1.5px solid ${L.border}`, borderRadius: L.radius, padding: '16px 14px', cursor: 'pointer', textAlign: 'left', fontFamily: L.font, transition: 'all 0.15s', boxShadow: L.shadow }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = meta.color; e.currentTarget.style.background = `${meta.color}14`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.background = L.card; }}
          >
            <div style={{ fontSize: 24, marginBottom: 8 }}>{meta.icon}</div>
            <div style={{ fontSize: 12, fontWeight: 800, color: L.text, marginBottom: 3, lineHeight: 1.3 }}>{sub.title}</div>
            <div style={{ fontSize: 10, color: meta.color, fontFamily: L.mono }}>
              {(n => (sub.unit_count === 1 ? `1 ${n}` : `${sub.unit_count} ${n}s`))(sub.subject_id === 'vce' ? 'topic' : noun)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function UnitCard({ unit, index, accent, onOpen, locked = false, label = 'Unit' }) {
  const [hov, setHov] = useState(false);
  const col = unit.color || accent;
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? `${col}10` : L.card,
        border: `1.5px solid ${hov ? col : L.border}`,
        borderRadius: L.radius, padding: '16px 18px',
        cursor: 'pointer', display: 'flex', alignItems: 'center',
        gap: 14, transition: 'all 0.18s', boxShadow: L.shadow,
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
        background: `${col}20`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 13, fontWeight: 800,
        color: col, fontFamily: L.mono,
      }}>{index + 1}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: col, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3, fontFamily: L.mono }}>
          {label} {index + 1} · {unit.lesson_count} {unit.lesson_count === 1 ? 'lesson' : 'lessons'} · ~{unit.estimated_minutes} min
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: L.text, marginBottom: unit.description ? 4 : 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: L.font }}>
          {unit.title}
        </div>
        {unit.description && (
          <div style={{ fontSize: 11, color: L.textDim, fontFamily: L.font, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {unit.description}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, fontSize: locked ? 15 : 11, fontWeight: 700, color: hov ? col : L.textMid, fontFamily: L.mono, transition: 'color 0.15s' }} aria-label={locked ? 'Locked' : undefined}>{locked ? '🔒' : '→'}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────

// learnAccess: 'all' (Premium/Family), 'sample' (7-day trial: the first unit of each list), 'none' (Exam Only, or the
// trial has ended). A locked unit shows a padlock and opens the plans instead (onUpgrade).
export default function LearnSection({ onClose, onNavigateProgress, learnAccess = 'all', onUpgrade = null, defaultYear = null }) {
  const unitOpen = (units, unit) => learnAccess === 'all' || (learnAccess === 'sample' && (units || []).indexOf(unit) === 0);
  const [selectedYear,    setSelectedYear]    = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSubGroup, setSelectedSubGroup] = useState(null); // e.g. 'biology' inside the Science subject
  const [yearData,        setYearData]        = useState(null);
  const [subjectData,     setSubjectData]     = useState(null);
  const [subGroupData,    setSubGroupData]    = useState(null);
  const [activeUnit,      setActiveUnit]      = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [retryFn,         setRetryFn]         = useState(null);
  const [yearAvailability, setYearAvailability] = useState(null); // null until computed; then { [yearId]: boolean }
  const [yearSubjectCounts, setYearSubjectCounts] = useState(null); // null until computed; then { [yearId]: { [subjectId]: unit_count } }

  // Which years actually have lessons, read straight from the same
  // /curriculum catalog the rest of this screen already uses, so the grid
  // stays correct as more years are built out without any hardcoded list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(YEARS.map(async y => {
        try {
          const data = await fetchCurriculum(`${y.id}/index.json`);
          const subjects = data.subjects || [];
          const realSubjects = subjects.filter(s => (s.unit_count || 0) > 0);
          const counts = Object.fromEntries(subjects.map(s => [s.subject_id, s.unit_count || 0]));
          return [y.id, realSubjects.length >= MIN_SUBJECTS_FOR_YEAR, counts];
        } catch {
          return [y.id, false, {}];
        }
      }));
      if (!cancelled) {
        setYearAvailability(Object.fromEntries(entries.map(([id, avail]) => [id, avail])));
        setYearSubjectCounts(Object.fromEntries(entries.map(([id, , counts]) => [id, counts])));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Per-subject year availability: whether the CURRENTLY SELECTED subject
  // itself has content in a given year, not just whether the year has 2+
  // subjects overall. Without this, e.g. Biology (Year 10 only) showed every
  // globally-available year pill as clickable, including years Biology has
  // no units in at all.
  const subjectYearAvailability = selectedSubject && yearSubjectCounts
    ? Object.fromEntries(YEARS.map(y => [y.id, (yearSubjectCounts[y.id]?.[selectedSubject] || 0) > 0]))
    : yearAvailability;

  // Resume where the student left off (see loadNavOnce above) — drills
  // back down through year -> subject -> unit using the actually-fetched
  // data at each step, not state that may not have propagated yet.
  //
  // Deliberately no cancellation-on-unmount guard here: this is a one-shot,
  // run-to-completion sequence gated globally by loadNavOnce's module-level
  // flag, not a subscription. In React 18 dev StrictMode, effects fire,
  // clean up, and fire again before this async chain resolves — guarding
  // with a closure-scoped `cancelled` flag (set true by the first, discarded
  // invocation's cleanup) silently aborted the chain after the year-level
  // fetch, before it reached subject/unit. The underlying component instance
  // and its state persist across that double-invoke, so it's safe to just
  // let the chain run.
  useEffect(() => {
    const nav = loadNavOnce();
    if (!nav || !nav.year || !YEARS.some((y) => y.id === nav.year)) { // a saved Year 3–5 position is ignored
      // nothing to resume: open at the child's own year instead of asking for it again
      if (defaultYear && YEARS.some((y) => y.id === defaultYear)) selectYear(defaultYear);
      return;
    }
    (async () => {
      const yd = await selectYear(nav.year);
      if (!yd || !nav.subject) return;
      const sd = await selectSubject(nav.subject, nav.year, yd);
      if (!sd) return;
      let unitsSource = sd;
      if (nav.subGroup) {
        const sgd = await selectSubGroup(nav.subGroup, nav.year, nav.subject, sd);
        if (!sgd) return;
        unitsSource = sgd;
      }
      if (!nav.unitFile) return;
      const unit = (unitsSource.units || []).find(u => u.file === nav.unitFile);
      if (!unit || !unitOpen(unitsSource.units, unit)) return; // a locked unit is not reopened on refresh
      await openUnit(unit, nav.year, nav.subject, nav.subGroup || null);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function selectYear(yearId) {
    setSelectedYear(yearId);
    try { localStorage.setItem('lumio_learn_year', yearId); } catch {} // the home screen shows this year's subjects
    setSelectedSubject(null);
    setYearData(null);
    setSubjectData(null);
    setActiveUnit(null);
    setError(null);
    setLoading(true);
    saveNav({ year: yearId });
    const load = async () => {
      try {
        const data = await fetchCurriculum(`${yearId}/index.json`);
        setYearData(data);
        setError(null);
        return data;
      } catch (e) {
        setError(`Could not load ${yearId.replace('year', 'Year ')} — ${e.message}`);
        setRetryFn(() => () => selectYear(yearId));
        return null;
      } finally { setLoading(false); }
    };
    setRetryFn(() => () => selectYear(yearId));
    return load();
  }

  // year/yearDataOverride default to current state, same reasoning as
  // openUnit above — the resume flow supplies them explicitly.
  async function selectSubject(subjectId, year = selectedYear, yearDataOverride = yearData) {
    setSelectedSubject(subjectId);
    setSubjectData(null);
    setSelectedSubGroup(null);
    setSubGroupData(null);
    setActiveUnit(null);
    setError(null);
    setLoading(true);
    saveNav({ year, subject: subjectId });
    try {
      // year index gives us the file path for the subject index
      const subjectEntry = yearDataOverride?.subjects?.find(s => s.subject_id === subjectId);
      const path = subjectEntry?.file
        ? `${year}/${subjectEntry.file}`
        : `${year}/${subjectId}/index.json`;
      const data = await fetchCurriculum(path);
      setSubjectData(data);
      setError(null);
      return data;
    } catch (e) {
      setError(`Could not load subject — ${e.message}`);
      setRetryFn(() => () => selectSubject(subjectId, year, yearDataOverride));
      return null;
    } finally { setLoading(false); }
  }

  // A subject can itself group further subjects instead of units directly
  // (e.g. Science -> Biology/Chemistry/Physics/Earth & Space) — same shape
  // and fetch pattern as selectSubject, one level deeper. subjectDataOverride
  // supplies the parent's already-fetched `.subjects` list to resolve the
  // sub-group's file path from, same reasoning as yearDataOverride above.
  async function selectSubGroup(subGroupId, year = selectedYear, subject = selectedSubject, subjectDataOverride = subjectData) {
    setSelectedSubGroup(subGroupId);
    setSubGroupData(null);
    setActiveUnit(null);
    setError(null);
    setLoading(true);
    saveNav({ year, subject, subGroup: subGroupId });
    try {
      const groupEntry = subjectDataOverride?.subjects?.find(s => s.subject_id === subGroupId);
      const path = groupEntry?.file
        ? `${year}/${subject}/${groupEntry.file}`
        : `${year}/${subject}/${subGroupId}/index.json`;
      const data = await fetchCurriculum(path);
      setSubGroupData(data);
      setError(null);
      return data;
    } catch (e) {
      setError(`Could not load subject — ${e.message}`);
      setRetryFn(() => () => selectSubGroup(subGroupId, year, subject, subjectDataOverride));
      return null;
    } finally { setLoading(false); }
  }

  // year/subject default to current state so existing call sites are
  // unaffected; the resume-on-refresh flow passes them explicitly to
  // avoid reading stale state before a setState from the same restore
  // pass has taken effect.
  async function openUnit(unit, year = selectedYear, subject = selectedSubject, subGroup = selectedSubGroup) {
    setError(null);
    setLoading(true);
    // unit.file = "the_cold_war/unit.json" relative to its own subject dir —
    // one level deeper when that subject is itself a sub-group of another.
    const basePath = subGroup ? `${subject}/${subGroup}` : subject;
    rememberUnit(unit, { year, subject, subGroup, unitFile: unit.file });
    try {
      const unitManifestPath = `${year}/${basePath}/${unit.file}`;
      const manifest = await fetchCurriculum(unitManifestPath);
      if (manifest.bespoke) {
        setActiveUnit({ ...manifest, _bespoke: manifest.bespoke });
        saveNav({ year, subject, subGroup, unitFile: unit.file });
        setError(null);
        return;
      }
      if (manifest.unit_json) {
        setActiveUnit({ ...manifest, _unitJson: manifest.unit_json });
        saveNav({ year, subject, subGroup, unitFile: unit.file });
        setError(null);
        return;
      }
      if (manifest.html_embed) {
        setActiveUnit({ ...manifest, lessons: [], _htmlEmbed: manifest.html_embed });
        saveNav({ year, subject, subGroup, unitFile: unit.file });
        setError(null);
        return;
      }
      // No html_embed — fall back to lesson player
      const unitDir = unit.file.replace('/unit.json', '');
      const lessons = await Promise.all(
        manifest.lessons.map(l =>
          fetchCurriculum(`${year}/${basePath}/${unitDir}/${l.file}`)
        )
      );
      setActiveUnit({ ...manifest, lessons });
      saveNav({ year, subject, subGroup, unitFile: unit.file });
      setError(null);
    } catch (e) {
      setError(`Could not load "${unit.title}" — ${e.message} (path: ${year}/${basePath}/${unit.file})`);
      setRetryFn(() => () => openUnit(unit, year, subject, subGroup));
    } finally { setLoading(false); }
  }

  function goBack() {
    if (activeUnit)       { setActiveUnit(null); saveNav({ year: selectedYear, subject: selectedSubject, subGroup: selectedSubGroup }); return; }
    if (selectedSubGroup) { setSelectedSubGroup(null); setSubGroupData(null); saveNav({ year: selectedYear, subject: selectedSubject }); return; }
    if (selectedSubject)  { setSelectedSubject(null); setSubjectData(null); saveNav({ year: selectedYear }); return; }
    if (selectedYear)     { setSelectedYear(null); setYearData(null); saveNav(null); return; }
    onClose();
  }

  // ── UnitPlayer / HTML embed ───────────────────────────────────
  if (activeUnit) {
    if (activeUnit._bespoke === 'dna_lesson') {
      return <DnaLesson onClose={() => setActiveUnit(null)} />;
    }
    if (activeUnit._bespoke === 'fraction_foundry') {
      return <FractionFoundry onClose={() => setActiveUnit(null)} />;
    }
    if (activeUnit._unitJson) {
      return (
        <NativeUnitPlayer
          unitUrl={`/${activeUnit._unitJson}`}
          onClose={() => setActiveUnit(null)}
        />
      );
    }
    if (activeUnit._htmlEmbed) {
      return <HtmlUnitEmbed embedPath={activeUnit._htmlEmbed} title={activeUnit.title} onClose={() => setActiveUnit(null)} onNavigateProgress={onNavigateProgress} />;
    }

    const lessonMeta = {
      id:      activeUnit.unit_id,
      title:   activeUnit.title,
      color:   activeUnit.color || SUBJECT_META[selectedSubGroup || selectedSubject]?.color || '#F59E0B',
      subject: activeUnit.subject,
      year:    activeUnit.year,
      icon:    activeUnit.icon || SUBJECT_META[selectedSubGroup || selectedSubject]?.icon || '📚',
    };
    return (
      <UnitPlayer
        unit={activeUnit}
        lessonMeta={lessonMeta}
        onClose={() => setActiveUnit(null)}
      />
    );
  }

  const activeYear   = YEARS.find(y => y.id === selectedYear);
  const subMeta      = selectedSubject ? (SUBJECT_META[selectedSubject] || {}) : {};
  const subEntry     = yearData?.subjects?.find(s => s.subject_id === selectedSubject);
  const groupMeta     = selectedSubGroup ? (SUBJECT_META[selectedSubGroup] || {}) : {};
  const subGroupEntry = subjectData?.subjects?.find(s => s.subject_id === selectedSubGroup);
  // Header/accent for whichever level's unit list is currently showing —
  // the sub-group's own meta once drilled in, else the top subject's.
  const activeMeta  = selectedSubGroup ? groupMeta : subMeta;
  const activeEntry = selectedSubGroup ? subGroupEntry : subEntry;
  const activeId    = selectedSubGroup || selectedSubject;

  // ── Year picker ───────────────────────────────────────────────
  if (!selectedYear) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', fontFamily: L.font }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${L.border}`, background: L.surface, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BackButton onClick={onClose} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: L.text, fontFamily: L.serif }}>Learn</div>
            <div style={{ fontSize: 11, color: L.textMid }}>Select your year to begin</div>
          </div>
        </div>
        <div style={{
          flex: 1, overflowY: 'auto',
          background: 'radial-gradient(1100px 620px at 84% -8%, #E7E1FB 0%, rgba(238,237,243,0) 60%), radial-gradient(820px 520px at 2% 106%, #FBE6DE 0%, rgba(238,237,243,0) 55%), #EEEDF3',
        }}>
          <div style={{ padding: '20px 16px', paddingBottom: 'calc(160px + env(safe-area-inset-bottom, 0px))' }}>
            {yearAvailability === null ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: 240 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${L.border}`, borderTopColor: L.accent, animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 13, color: L.textMid, fontFamily: L.font }}>Loading years…</span>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
                {YEARS.map(y => {
                  const active = !!yearAvailability[y.id];
                  return (
                    <YearCard key={y.id} year={y} active={active} onSelect={() => selectYear(y.id)} />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Loading / error ───────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', background: L.bg }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${L.border}`, background: L.surface, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BackButton onClick={goBack} />
          <div style={{ fontSize: 15, fontWeight: 800, color: L.text, fontFamily: L.font }}>{activeYear?.label}</div>
        </div>
        <Spinner label="Loading…" />
      </div>
    );
  }

  // ── Subject grid ──────────────────────────────────────────────
  if (!selectedSubject) {
    const subjects = yearData?.subjects || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', background: L.bg, fontFamily: L.font }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${L.border}`, background: L.surface, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BackButton onClick={goBack} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: L.text }}>{activeYear?.label}</div>
            <div style={{ fontSize: 11, color: L.textMid }}>
              {subjects.length === 0 ? 'No subjects yet — check back soon' : 'Select a subject'}
            </div>
          </div>
        </div>
        <YearPills selected={selectedYear} onSelect={selectYear} availability={yearAvailability} />
        {error && <ErrorBanner message={error} onRetry={retryFn} />}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', paddingBottom: 'calc(160px + env(safe-area-inset-bottom, 0px))' }}>
          <SubjectTiles
            subjects={subjects}
            onSelect={selectSubject}
            emptyTitle="Coming soon"
            emptyBody={`Subjects for ${activeYear?.label} are being built. Try Year 10 to see the first content.`}
          />
        </div>
      </div>
    );
  }

  // ── Sub-subject grid ─────────────────────────────────────────
  // A subject can group further subjects instead of listing units
  // directly (Science -> Biology/Chemistry/Physics/Earth & Space).
  if (subjectData?.subjects && !selectedSubGroup) {
    const subGroups = subjectData.subjects || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', background: L.bg, fontFamily: L.font }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${L.border}`, background: L.surface, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BackButton onClick={goBack} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: L.text, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{subMeta.icon}</span>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subEntry?.title || selectedSubject}</span>
            </div>
            <div style={{ fontSize: 11, color: L.textMid }}>{activeYear?.label}</div>
          </div>
        </div>
        <YearPills selected={selectedYear} onSelect={selectYear} availability={subjectYearAvailability} />
        {error && <ErrorBanner message={error} onRetry={retryFn} />}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', paddingBottom: 'calc(160px + env(safe-area-inset-bottom, 0px))' }}>
          <SubjectTiles
            subjects={subGroups}
            onSelect={selectSubGroup}
            parentIcon={SUBJECT_META[selectedSubject]?.icon || '📚'}
            noun={selectedSubject === 'vce' ? 'topic' : 'unit'}
            emptyTitle="Coming soon"
            emptyBody={`Subjects under ${subEntry?.title || selectedSubject} are being built.`}
          />
        </div>
      </div>
    );
  }

  // ── Unit list ─────────────────────────────────────────────────
  const units = (selectedSubGroup ? subGroupData : subjectData)?.units || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', background: L.bg, fontFamily: L.font }}>
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${L.border}`, background: L.surface, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
        <BackButton onClick={goBack} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: L.text, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{activeMeta.icon}</span>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeEntry?.title || activeId}</span>
          </div>
          <div style={{ fontSize: 11, color: L.textMid }}>{activeYear?.label}</div>
        </div>
      </div>
      <YearPills selected={selectedYear} onSelect={selectYear} availability={subjectYearAvailability} />
      {error && <ErrorBanner message={error} onRetry={retryFn} />}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', paddingBottom: 'calc(160px + env(safe-area-inset-bottom, 0px))' }}>
        {units.length === 0 ? (
          <div style={{ background: L.card, border: `1.5px dashed ${L.border}`, borderRadius: L.radius, padding: '36px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🚧</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: L.text, marginBottom: 8, fontFamily: L.serif }}>No units yet</div>
            <div style={{ fontSize: 13, color: L.textMid, lineHeight: 1.65 }}>Units for {activeEntry?.title || activeId} are being added. Check back soon.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {units.map((unit, i) => (
              <UnitCard key={unit.unit_id} unit={unit} index={i} accent={activeMeta.color || L.accent} locked={!unitOpen(units, unit)} label={selectedSubject === 'vce' ? 'Topic' : 'Unit'}
                onOpen={() => (unitOpen(units, unit) ? openUnit(unit) : onUpgrade && onUpgrade())} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
